<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-5">
      <div class="flex flex-col gap-4 lg:grid lg:grid-cols-12 lg:gap-x-4 lg:items-center">
  <div class="lg:col-span-3 flex items-baseline gap-2 flex-wrap">
    <h2 class="text-base font-bold text-[#0d4169] text-left">
      {{ resolvedCompanyName }}
    </h2>
    <span class="text-sm font-bold text-slate-600">
      Profit and Loss Statement
    </span>
  </div>

  <form
    class="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:contents"
    @submit.prevent
  >
    <!-- From -->
    <div class="flex items-center gap-2 min-w-0 lg:col-span-3 lg:col-start-5">
      <label
        class="text-[0.78rem] font-semibold text-[#1e293b] shrink-0 whitespace-nowrap"
      >
        From
      </label>
      <input
        v-model="form.fromDate"
        type="date"
        class="flex-1 min-w-0 w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
      />
    </div>

    <!-- Upto -->
    <div class="flex items-center gap-2 min-w-0 lg:col-span-3 lg:col-start-8">
      <label
        class="text-[0.78rem] font-semibold text-[#1e293b] shrink-0 whitespace-nowrap"
      >
        Upto
      </label>
      <input
        v-model="form.uptoDate"
        type="date"
        class="flex-1 min-w-0 w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
      />
    </div>
  </form>
</div>
    </div>

    <div class="px-2 pb-6 pt-2">
      <p
        v-if="!showReportContent"
        class="text-sm text-slate-500 text-center py-10 m-0 italic"
      >
        Select a date range to view profit and loss statement.
      </p>

      <div v-else class="w-full">
        <div v-if="isLoadingReport" class="py-12 flex justify-center items-center">
          <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <template v-else>
        <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0 px-4">
          {{ reportPeriodTitle }}
        </p>

        <div class="pnl-table-scroll relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)] px-2">
          <table class="pnl-table border-collapse">
            <thead>
              <tr>
                <th class="col-section">Section</th>
                <th class="col-desc">Description</th>
                <th class="num">Cash</th>
                <th class="num">Credit</th>
                <th class="num">Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(block, blockIndex) in reportBlocks" :key="`block-${blockIndex}`">
                <tr
                  v-for="(row, rowIndex) in block.rows"
                  :key="`${blockIndex}-${rowIndex}`"
                  :class="[
                    'transition-colors',
                    isTotalRow(row) ? 'pnl-total-row' : 'hover:bg-slate-50 border-b'
                  ]"
                >
                  <td
                    v-if="block.sectionLabel && rowIndex === 0"
                    :rowspan="block.rows.length"
                    class="col-section section-cell"
                  >
                    {{ block.sectionLabel }}
                  </td>
                  <td v-else-if="!block.sectionLabel" class="col-section section-cell">
                    {{ row.sectionSide || '' }}
                  </td>
                  <td
                    class="col-desc"
                    :class="{
                      'pnl-total-text': isTotalRow(row),
                      'pnl-section-data': !block.showCashCredit
                    }"
                  >
                    {{ row.label }}
                  </td>
                  <td
                    class="num"
                    :class="{
                      'pnl-total-text': isTotalRow(row),
                      'pnl-underline': row.underline && block.showCashCredit,
                      'pnl-section-data': !block.showCashCredit
                    }"
                  >
                    {{ block.showCashCredit ? formatAmount(row.cash) : '' }}
                  </td>
                  <td
                    class="num"
                    :class="{
                      'pnl-total-text': isTotalRow(row),
                      'pnl-underline': row.underline && block.showCashCredit,
                      'pnl-section-data': !block.showCashCredit
                    }"
                  >
                    {{ block.showCashCredit ? formatAmount(row.credit) : '' }}
                  </td>
                  <td
                    class="num"
                    :class="{
                      'pnl-total-text': isTotalRow(row),
                      'pnl-underline': row.underline,
                      'pnl-section-data': !block.showCashCredit
                    }"
                  >
                    {{ formatAmount(row.amount) }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const form = reactive({
  fromDate: '',
  uptoDate: ''
})

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const showReportContent = computed(() => Boolean(form.fromDate && form.uptoDate))

function formatDisplayDate(isoDate) {
  if (!isoDate) return ''
  const d = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const reportPeriodTitle = computed(() => {
  const from = form.fromDate ? formatDisplayDate(form.fromDate) : '—'
  const upto = form.uptoDate ? formatDisplayDate(form.uptoDate) : '—'
  return `Profit and Loss Statement (${from} - ${upto})`
})

function formatAmount(value) {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function isTotalRow(row) {
  return Boolean(row?.underline || row?.isTotal)
}

const salesIncomeRows = ref([])
const salesIncomeTotals = ref({ cash: 0, credit: 0, total: 0 })
const beginningInventoryRows = ref([])
const beginningInventorySourcesTotal = ref(0)
const beginningInventoryStoreAmount = ref(0)
const endingInventoryRows = ref([])
const endingInventorySourcesTotal = ref(0)
const endingInventoryStoreAmount = ref(0)
const purchaseRows = ref([])
const purchasesTotal = ref(0)
const gaExpenseRows = ref([])
const gaExpensesTotal = ref(0)
const isLoadingReport = ref(false)
let salesIncomeRequestId = 0
let inventorySectionsRequestId = 0
let purchasesRequestId = 0
let gaExpensesRequestId = 0
let reportRequestId = 0

const INVENTORY_STORE_LABEL = 'Store'

async function fetchSalesIncome() {
  const requestId = ++salesIncomeRequestId

  if (!form.fromDate || !form.uptoDate) {
    salesIncomeRows.value = []
    salesIncomeTotals.value = { cash: 0, credit: 0, total: 0 }
    return
  }

  try {
    const params = new URLSearchParams({
      fromDate: form.fromDate,
      uptoDate: form.uptoDate
    })
    const response = await apiFetch(`/finance/profit-loss/sales-income-all-sources?${params.toString()}`, {
      method: 'GET'
    })
    if (requestId !== salesIncomeRequestId) return

    if (!response.ok) {
      salesIncomeRows.value = []
      salesIncomeTotals.value = { cash: 0, credit: 0, total: 0 }
      return
    }

    const data = await response.json().catch(() => ({}))
    if (requestId !== salesIncomeRequestId) return

    const rows = Array.isArray(data?.rows) ? data.rows : []
    salesIncomeRows.value = rows.map((row) => ({
      label: String(row?.categoryName || '').trim(),
      cash: Number(row?.cash) || 0,
      credit: Number(row?.credit) || 0,
      amount: Number(row?.total) || 0
    })).filter((row) => row.label)

    const totalsPayload = data?.totals && typeof data.totals === 'object' ? data.totals : {}
    salesIncomeTotals.value = {
      cash: Number(totalsPayload.cash) || 0,
      credit: Number(totalsPayload.credit) || 0,
      total: Number(totalsPayload.total) || 0
    }
  } catch {
    if (requestId !== salesIncomeRequestId) return
    salesIncomeRows.value = []
    salesIncomeTotals.value = { cash: 0, credit: 0, total: 0 }
  }
}

function mapInventorySectionRows(sectionData) {
  const rows = Array.isArray(sectionData?.rows) ? sectionData.rows : []
  return rows.map((row) => ({
    label: String(row?.categoryName || '').trim(),
    amount: Number(row?.amount) || 0
  })).filter((row) => row.label)
}

function clearInventorySections() {
  beginningInventoryRows.value = []
  beginningInventorySourcesTotal.value = 0
  beginningInventoryStoreAmount.value = 0
  endingInventoryRows.value = []
  endingInventorySourcesTotal.value = 0
  endingInventoryStoreAmount.value = 0
}

function applyInventorySection(sectionData, rowsRef, sourcesTotalRef, storeAmountRef) {
  rowsRef.value = mapInventorySectionRows(sectionData)
  sourcesTotalRef.value = Number(sectionData?.total) || 0
  storeAmountRef.value = Number(sectionData?.storeAmount) || 0
}

async function fetchInventorySections() {
  const requestId = ++inventorySectionsRequestId

  if (!form.fromDate || !form.uptoDate) {
    clearInventorySections()
    return
  }

  try {
    const params = new URLSearchParams({
      fromDate: form.fromDate,
      uptoDate: form.uptoDate
    })
    const response = await apiFetch(`/finance/profit-loss/beginning-inventory?${params.toString()}`, {
      method: 'GET'
    })
    if (requestId !== inventorySectionsRequestId) return

    if (!response.ok) {
      clearInventorySections()
      return
    }

    const data = await response.json().catch(() => ({}))
    if (requestId !== inventorySectionsRequestId) return

    const beginning = data?.beginning && typeof data.beginning === 'object'
      ? data.beginning
      : {
        rows: data?.rows,
        total: data?.total,
        storeAmount: data?.storeAmount
      }
    const ending = data?.ending && typeof data.ending === 'object' ? data.ending : null

    applyInventorySection(
      beginning,
      beginningInventoryRows,
      beginningInventorySourcesTotal,
      beginningInventoryStoreAmount
    )

    if (ending) {
      applyInventorySection(
        ending,
        endingInventoryRows,
        endingInventorySourcesTotal,
        endingInventoryStoreAmount
      )
    } else {
      endingInventoryRows.value = []
      endingInventorySourcesTotal.value = 0
      endingInventoryStoreAmount.value = 0
    }
  } catch {
    if (requestId !== inventorySectionsRequestId) return
    clearInventorySections()
  }
}

function clearPurchasesSection() {
  purchaseRows.value = []
  purchasesTotal.value = 0
}

async function fetchPurchasesByCategory() {
  const requestId = ++purchasesRequestId

  if (!form.fromDate || !form.uptoDate) {
    clearPurchasesSection()
    return
  }

  try {
    const params = new URLSearchParams({
      fromDate: form.fromDate,
      uptoDate: form.uptoDate
    })
    const response = await apiFetch(`/finance/profit-loss/purchases-by-category?${params.toString()}`, {
      method: 'GET'
    })
    if (requestId !== purchasesRequestId) return

    if (!response.ok) {
      clearPurchasesSection()
      return
    }

    const data = await response.json().catch(() => ({}))
    if (requestId !== purchasesRequestId) return

    const rows = Array.isArray(data?.rows) ? data.rows : []
    purchaseRows.value = rows.map((row) => ({
      label: String(row?.categoryName || '').trim(),
      amount: Number(row?.amount) || 0
    })).filter((row) => row.label)

    purchasesTotal.value = Number(data?.total) || 0
  } catch {
    if (requestId !== purchasesRequestId) return
    clearPurchasesSection()
  }
}

function clearGeneralAdminExpensesSection() {
  gaExpenseRows.value = []
  gaExpensesTotal.value = 0
}

async function fetchGeneralAdminExpenses() {
  const requestId = ++gaExpensesRequestId

  if (!form.fromDate || !form.uptoDate) {
    clearGeneralAdminExpensesSection()
    return
  }

  try {
    const params = new URLSearchParams({
      fromDate: form.fromDate,
      uptoDate: form.uptoDate
    })
    const response = await apiFetch(
      `/finance/profit-loss/general-admin-expenses-by-chart-account?${params.toString()}`,
      { method: 'GET' }
    )
    if (requestId !== gaExpensesRequestId) return

    if (!response.ok) {
      clearGeneralAdminExpensesSection()
      return
    }

    const data = await response.json().catch(() => ({}))
    if (requestId !== gaExpensesRequestId) return

    const rows = Array.isArray(data?.rows) ? data.rows : []
    gaExpenseRows.value = rows.map((row) => ({
      label: String(row?.label || row?.accountName || '').trim(),
      amount: Number(row?.amount) || 0
    })).filter((row) => row.label)

    gaExpensesTotal.value = Number(data?.total) || 0
  } catch {
    if (requestId !== gaExpensesRequestId) return
    clearGeneralAdminExpensesSection()
  }
}

async function loadAllReportData() {
  if (!form.fromDate || !form.uptoDate) {
    salesIncomeRows.value = []
    salesIncomeTotals.value = { cash: 0, credit: 0, total: 0 }
    clearInventorySections()
    clearPurchasesSection()
    clearGeneralAdminExpensesSection()
    isLoadingReport.value = false
    return
  }

  const requestId = ++reportRequestId
  isLoadingReport.value = true

  try {
    await Promise.all([
      fetchSalesIncome(),
      fetchInventorySections(),
      fetchPurchasesByCategory(),
      fetchGeneralAdminExpenses()
    ])
  } finally {
    if (requestId === reportRequestId) {
      isLoadingReport.value = false
    }
  }
}

watch(
  () => [form.fromDate, form.uptoDate],
  () => {
    loadAllReportData()
  },
  { immediate: true }
)

const totals = computed(() => {
  const totalSalesCash = salesIncomeTotals.value.cash
  const totalSalesCredit = salesIncomeTotals.value.credit
  const totalSales = salesIncomeTotals.value.total
  const beginningStoreAmount = beginningInventoryStoreAmount.value
  const totalBeginningInventory = beginningInventorySourcesTotal.value + beginningStoreAmount
  const totalPurchases = Number(purchasesTotal.value) || 0
  const availableForSales = totalBeginningInventory + totalPurchases
  const endingStoreAmount = endingInventoryStoreAmount.value
  const totalEndingInventory = endingInventorySourcesTotal.value + endingStoreAmount
  const costOfGoods = availableForSales - totalEndingInventory
  const grossProfit = totalSales - costOfGoods
  const totalExpenses = Number(gaExpensesTotal.value) || 0
  const profitBeforeTax = grossProfit - totalExpenses

  return {
    totalSalesCash,
    totalSalesCredit,
    totalSales,
    totalBeginningInventory,
    totalPurchases,
    availableForSales,
    totalEndingInventory,
    costOfGoods,
    grossProfit,
    totalExpenses,
    profitBeforeTax
  }
})

const reportBlocks = computed(() => {
  const t = totals.value

  return [
    {
      sectionLabel: '1. Sales/Income',
      showCashCredit: true,
      rows: [
        ...salesIncomeRows.value.map((row) => ({
          label: row.label,
          cash: row.cash,
          credit: row.credit,
          amount: row.amount
        })),
        {
          label: 'Total Sales',
          cash: t.totalSalesCash,
          credit: t.totalSalesCredit,
          amount: t.totalSales,
          isSubtotal: true,
          underline: true
        }
      ]
    },
    {
      sectionLabel: '2. Beginning inventory',
      rows: [
        ...beginningInventoryRows.value.map((row) => ({ label: row.label, amount: row.amount })),
        { label: INVENTORY_STORE_LABEL, amount: beginningInventoryStoreAmount.value },
        {
          label: 'Total Beginning inventory',
          amount: t.totalBeginningInventory,
          isSubtotal: true,
          underline: true
        }
      ]
    },
    {
      sectionLabel: '3. Purchase',
      rows: [
        ...purchaseRows.value.map((row) => ({ label: row.label, amount: row.amount })),
        {
          label: 'Total Purchase',
          amount: t.totalPurchases,
          isSubtotal: true,
          underline: true
        },
        {
          label: 'Available for Sale',
          amount: t.availableForSales,
          isSubtotal: true,
          underline: true
        }
      ]
    },
    {
      sectionLabel: '4. Ending inventory',
      rows: [
        ...endingInventoryRows.value.map((row) => ({ label: row.label, amount: row.amount })),
        { label: INVENTORY_STORE_LABEL, amount: endingInventoryStoreAmount.value },
        {
          label: 'Total Ending inventory',
          amount: t.totalEndingInventory,
          isSubtotal: true,
          underline: true
        }
      ]
    },
    {
      sectionLabel: '5. Cost of Goods',
      rows: [
        {
          label: 'Cost of Goods',
          amount: t.costOfGoods,
          isTotal: true,
          underline: true
        }
      ]
    },
    {
      sectionLabel: '6. Gross Profit',
      rows: [
        { label: 'Gross Profit', amount: t.grossProfit, isTotal: true, underline: true }
      ]
    },
    {
      sectionLabel: '7. General & Administrative Expense',
      rows: [
        ...gaExpenseRows.value.map((row) => ({ label: row.label, amount: row.amount })),
        {
          label: 'Total General & Administrative Expense',
          amount: t.totalExpenses,
          isSubtotal: true,
          underline: true
        }
      ]
    },
    {
      sectionLabel: '8. Profit Before Tax',
      rows: [
        { label: 'Profit Before Tax', amount: t.profitBeforeTax, isTotal: true, underline: true }
      ]
    }
  ]
})
</script>

<style scoped>
.pnl-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.7rem;
  color: #1e293b;
  border-collapse: collapse;
  table-layout: auto;
  width: max-content;
  max-width: 100%;
}

.pnl-table th,
.pnl-table td {
  padding: 4px 10px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
  font-size: 0.68rem;
  line-height: 1.35;
  text-align: left;
}

.pnl-table thead th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 20;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
}

.pnl-table thead th.col-section,
.pnl-table thead th.col-desc {
  color: #1e293b;
  white-space: nowrap;
  vertical-align: middle;
}

.pnl-table thead th.num {
  text-align: left;
  padding-left: 0;
}

.pnl-table tbody tr:hover {
  background: #f8fafc;
}

.pnl-table .col-section {
  width: 11rem;
  min-width: 11rem;
  max-width: 14rem;
  white-space: normal;
  word-break: break-word;
  font-weight: 600;
  color: #0d4169;
  vertical-align: top;
}

.pnl-table .section-cell {
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
}

.pnl-table .col-desc {
  white-space: normal;
  word-break: break-word;
  width: auto;
  min-width: 12rem;
  max-width: 22rem;
  padding-right: 1.25rem;
  color: #334155;
}

.pnl-table th.num,
.pnl-table td.num {
  width: auto;
  min-width: 5.5rem;
  padding-left: 0;
  text-align: left;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.pnl-total-row td {
  padding-top: 0.35rem;
  padding-bottom: 0.85rem;
  border-bottom: none;
}

.pnl-total-row + tr td,
.pnl-total-row + tr .section-cell {
  padding-top: 0.65rem;
}

.pnl-total-text {
  font-weight: 700;
  font-size: 0.82rem;
  color: #0f172a;
}

.pnl-underline {
  border-bottom: 2px solid #64748b !important;
}

.pnl-table td.pnl-section-data {
  background: #fff;
}

.pnl-table tbody tr:not(.pnl-total-row):hover td.pnl-section-data {
  background: #f8fafc;
}
</style>
